import React, { useEffect, useState } from "react";
import axios from "axios";

export default function DiscountModal({ isOpen, onClose, onApplied }) {
  const [discountMode, setDiscountMode] = useState("live");
  const [discountType, setDiscountType] = useState("student");
  const [discountYearMonth, setDiscountYearMonth] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 7);
  });
  const [discountStep, setDiscountStep] = useState("select");
  const [identifier, setIdentifier] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [applyMessage, setApplyMessage] = useState("");
  const [foundInvoices, setFoundInvoices] = useState([]);
  const [selectedInvoiceRefs, setSelectedInvoiceRefs] = useState([]);
  const [selectAllInvoices, setSelectAllInvoices] = useState(false);

  const resetForm = () => {
    setDiscountStep("select");
    setDiscountMode("live");
    setDiscountType("student");
    setIdentifier("");
    setFoundInvoices([]);
    setSelectedInvoiceRefs([]);
    setSelectAllInvoices(false);
    setDiscountAmount("");
    setSearchError("");
    setApplyMessage("");
    setSearchLoading(false);
    setApplyLoading(false);
    const today = new Date();
    setDiscountYearMonth(today.toISOString().slice(0, 7));
  };

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSearch = async () => {
    setSearchError("");
    setFoundInvoices([]);
    setSelectedInvoiceRefs([]);
    setSelectAllInvoices(false);

    if (!discountYearMonth || !/^\d{4}-\d{2}$/.test(discountYearMonth)) {
      setSearchError("Please select a valid year-month (YYYY-MM format).");
      return;
    }

    if (!identifier.trim()) {
      setSearchError(
        `Please enter a valid ${discountType === "student" ? "Student ID" : "Class Name"}.`
      );
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/discount/search`,
        {
          mode: discountMode,
          type: discountType,
          yearMonth: discountYearMonth,
          identifier: identifier.trim(),
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || "Search failed");
      }

      setFoundInvoices(response.data.invoices || []);
      setDiscountStep("apply");
    } catch (err) {
      setSearchError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Failed to search target. Please check your input and try again."
      );
    } finally {
      setSearchLoading(false);
    }
  };

  const handleToggleInvoice = (invoiceRef) => {
    setSelectedInvoiceRefs((prev) => {
      if (prev.includes(invoiceRef)) {
        setSelectAllInvoices(false);
        return prev.filter((ref) => ref !== invoiceRef);
      }
      const newSelection = [...prev, invoiceRef];
      if (newSelection.length === foundInvoices.length) {
        setSelectAllInvoices(true);
      }
      return newSelection;
    });
  };

  const handleSelectAllInvoices = () => {
    if (selectAllInvoices) {
      setSelectedInvoiceRefs([]);
      setSelectAllInvoices(false);
    } else {
      setSelectedInvoiceRefs(foundInvoices.map((inv) => inv.invoice_ref));
      setSelectAllInvoices(true);
    }
  };

  const handleApplyDiscount = async () => {
    if (selectedInvoiceRefs.length === 0) {
      setApplyMessage("Please select at least one invoice to apply discount.");
      return;
    }

    const amount = Number(discountAmount);
    setApplyLoading(true);
    setApplyMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/discount/apply`,
        {
          mode: discountMode,
          invoiceRefs: selectedInvoiceRefs,
          discountAmount: amount,
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.message || response.data.error || "Apply failed");
      }

      setApplyMessage(
        response.data.message ||
          `Discount of $${amount} applied successfully to ${response.data.updatedCount} invoice(s).`
      );

      if (onApplied) onApplied(discountYearMonth);

      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setApplyMessage(
        `Error: ${err.response?.data?.message || err.response?.data?.error || err.message}`
      );
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Discount Management</h2>
        </div>

        <div className="p-6 space-y-6">
          {discountStep === "select" && (
            <>
              <div>
                <label className="block text-gray-700 font-medium mb-2">Mode</label>
                <div className="flex gap-6">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="mode"
                      value="live"
                      checked={discountMode === "live"}
                      onChange={(e) => {
                        setDiscountMode(e.target.value);
                        if (discountType === "class") setDiscountType("student");
                      }}
                      className="form-radio h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">Live</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="mode"
                      value="video"
                      checked={discountMode === "video"}
                      onChange={(e) => {
                        setDiscountMode(e.target.value);
                        setDiscountType("student");
                      }}
                      className="form-radio h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">Video</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Apply to</label>
                <div className="flex gap-6">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="type"
                      value="student"
                      checked={discountType === "student"}
                      onChange={() => setDiscountType("student")}
                      className="form-radio h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2">Student</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="type"
                      value="class"
                      checked={discountType === "class"}
                      onChange={() => setDiscountType("class")}
                      disabled={discountMode === "video"}
                      className={`form-radio h-5 w-5 ${
                        discountMode === "video"
                          ? "opacity-40 cursor-not-allowed"
                          : "text-blue-600"
                      }`}
                    />
                    <span className={`ml-2 ${discountMode === "video" ? "opacity-50" : ""}`}>
                      Class
                    </span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Year-Month</label>
                <input
                  type="month"
                  value={discountYearMonth}
                  onChange={(e) => setDiscountYearMonth(e.target.value)}
                  className="w-full max-w-xs border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  {discountType === "student" ? "Student ID" : "Class Name"}
                </label>
                <input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={discountType === "student" ? "e.g. s100021" : "e.g. Class A"}
                  className="w-full border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={handleClose}
                  className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSearch}
                  disabled={searchLoading || !identifier || !discountYearMonth}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searchLoading ? "Searching..." : "Search"}
                </button>
              </div>

              {searchError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
                  {searchError}
                </div>
              )}
            </>
          )}

          {discountStep === "apply" && (
            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold mb-3">Select Invoices for Discount</h3>

              {foundInvoices.length > 0 && (
                <div className="mb-6 overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 border text-left w-12">
                          <input
                            type="checkbox"
                            checked={selectAllInvoices}
                            onChange={handleSelectAllInvoices}
                            className="h-4 w-4 text-blue-600 rounded"
                          />
                        </th>
                        <th className="px-4 py-2 border text-left">Invoice Ref</th>
                        <th className="px-4 py-2 border text-left">Student ID</th>
                        <th className="px-4 py-2 border text-left">Student Name</th>
                        <th className="px-4 py-2 border text-right">Total Amount</th>
                        <th className="px-4 py-2 border text-right">Current Discount</th>
                        <th className="px-4 py-2 border text-right">Final Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {foundInvoices.map((invoice) => {
                        const currentDiscount = Number(invoice.discount || 0);
                        const totalAmount = Number(invoice.total_amount || 0);
                        const finalAmount = Math.max(0, totalAmount - currentDiscount);

                        return (
                          <tr key={invoice.invoice_ref} className="hover:bg-gray-50">
                            <td className="px-4 py-2 border text-center">
                              <input
                                type="checkbox"
                                checked={selectedInvoiceRefs.includes(invoice.invoice_ref)}
                                onChange={() => handleToggleInvoice(invoice.invoice_ref)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                            </td>
                            <td className="px-4 py-2 border font-mono text-sm">
                              {invoice.invoice_ref}
                            </td>
                            <td className="px-4 py-2 border">{invoice.student_id}</td>
                            <td className="px-4 py-2 border">{invoice.student_string}</td>
                            <td className="px-4 py-2 border text-right">
                              ${totalAmount.toFixed(2)}
                            </td>
                            <td className="px-4 py-2 border text-right">
                              {currentDiscount > 0 ? `$${currentDiscount.toFixed(2)}` : "-"}
                            </td>
                            <td className="px-4 py-2 border text-right font-medium">
                              ${finalAmount.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">
                  Discount Amount to Apply (HKD)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  placeholder="e.g. 200"
                  className="w-full max-w-xs border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">
                  This amount will replace the existing discount for selected invoices.
                </p>
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => {
                    setDiscountStep("select");
                    setSearchError("");
                    setFoundInvoices([]);
                    setSelectedInvoiceRefs([]);
                    setSelectAllInvoices(false);
                  }}
                  className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 rounded-md transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleApplyDiscount}
                  disabled={applyLoading || !discountAmount || selectedInvoiceRefs.length === 0}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-2.5 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {applyLoading
                    ? "Applying..."
                    : `Apply Discount to ${selectedInvoiceRefs.length} Invoice(s)`}
                </button>
              </div>

              {applyMessage && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 text-green-800 rounded">
                  {applyMessage}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-5 border-t bg-gray-50 flex justify-end gap-4">
          <button
            onClick={handleClose}
            className="px-6 py-2.5 bg-gray-300 hover:bg-gray-400 rounded-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
